import { Space, Table, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { StyledButton } from "../../Common/Components";
import { PaginationComponent } from "../../Common/Pagination/PaginationComponent";
import PermissionCheck from "../../Common/PermissionCheck";
import SearchFilter from "~components/SearchFilter/SearchFilter";
import { useFetchSeasonVenues } from "../../entities/api";
import { CountryCodes } from "~enums/countryCodes.ts";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHandlePagination } from "../../hooks/useHandlePagination";

const PermissionName = PermissionNames.SITE;

const TableToolbar = styled.div`
  display: flex;
  justify-content: space-between;
`;

const getColumns = ({ formatMessage, customerId, sport, organizationId }) => [
  {
    title: formatMessage({
      id: "name.local",
      defaultMessage: "Local Name",
    }),
    dataIndex: "nameLocal",
  },
  {
    title: formatMessage({
      id: "country",
      defaultMessage: "Country",
    }),
    dataIndex: "countryCode",
    render: (value) => (value ? formatMessage({ id: `countryCode.${value}` }) : ""),
    filters: Object.values(CountryCodes).map((code) => ({
      text: formatMessage({ id: `countryCode.${code}` }),
      value: code,
    })),
    filterSearch: true,
    onFilter: (value, record) => record.countryCode === value,
  },
  {
    align: "right",
    render: (value, row) => (
      <PermissionCheck permissionKeys={[PermissionName]} permissionOptions={[PermissionOperations.UPDATE]}>
        <StyledButton type="text" size="small">
          <Link to={`/${customerId}/admin/` + sport + "/" + organizationId + "/sites/manage/" + row.siteId}>
            {formatMessage({
              id: "manage",
              defaultMessage: "Manage",
            })}
          </Link>
        </StyledButton>
      </PermissionCheck>
    ),
  },
];

const SeasonSites = () => {
  const { formatMessage } = useIntl();
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const { sport, organizationId, customerId } = useParams();
  const { pageSize, currentPage, handlePaginationChange, offset, pageSizeOptions } = useHandlePagination(20);
  const { data, isLoading } = useFetchSeasonVenues({
    include: "sites",
    limit: pageSize,
    offset,
  });

  const nextLink = useMemo(() => data?.links?.next, [data?.links?.next]);

  useEffect(() => {
    if (data) {
      const sitesData = data?.includes?.resources?.sites;
      let filteredData = sitesData ? Object.values(sitesData) : [];

      if (searchText !== "") {
        filteredData = filteredData.filter((result) => {
          return result.nameLocal.toUpperCase().indexOf(searchText.toUpperCase()) > -1;
        });
      }
      setFilteredData(filteredData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchText]);

  return (
    <React.Fragment>
      <Space direction="vertical" style={{ width: "100%" }}>
        <TableToolbar>
          <Typography.Title level={4}>
            {formatMessage({
              id: "sites",
              defaultMessage: "Sites",
            })}
          </Typography.Title>
          <SearchFilter doSearch={setSearchText} />
        </TableToolbar>
        <Table
          locale={{
            filterConfirm: formatMessage({ id: "ok" }),
            filterReset: formatMessage({ id: "reset" }),
          }}
          columns={getColumns({
            formatMessage,
            customerId: customerId,
            sport: sport,
            organizationId: organizationId,
          })}
          loading={isLoading}
          dataSource={filteredData}
          pagination={{
            current: currentPage,
            defaultPageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: pageSizeOptions,
            showQuickJumper: false,
            total: nextLink ? offset + filteredData.length + 1 : filteredData.length + offset,
            position: ["bottomCenter"],
            onChange: handlePaginationChange,
            showTotal: (total, range) => (
              <PaginationComponent pageNumber={currentPage} pageSize={pageSize} total={total} range={range} />
            ),
          }}
          rowKey={(x) => x.siteId}
          size="small"
        />
      </Space>
    </React.Fragment>
  );
};

export default SeasonSites;
